import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";

type WebhookSubscription = components["schemas"]["WebhookSubscriptionResponse"];
// Free-text admin override (any event type), so parsed strings are cast to WebhookEventType.
type WebhookEventWire =
  components["schemas"]["CreateWebhookSubscriptionRequest"]["eventTypes"][number];

interface EditWebhookSubscriptionDialogProps {
  clientId: string;
  subscription: WebhookSubscription;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: (updated: WebhookSubscription) => void;
}

export function EditWebhookSubscriptionDialog({
  clientId,
  subscription,
  open,
  onOpenChange,
  onUpdated,
}: EditWebhookSubscriptionDialogProps) {
  const [url, setUrl] = useState(subscription.url);
  const [eventTypesText, setEventTypesText] = useState(
    (subscription.eventTypes as string[]).join("\n"),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setUrl(subscription.url);
      setEventTypesText((subscription.eventTypes as string[]).join("\n"));
      setError(null);
    }
  }, [open, subscription]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmedUrl = url.trim();
    if (!trimmedUrl.startsWith("https://")) {
      setError("URL must be an absolute https URL.");
      return;
    }

    const eventTypes = eventTypesText
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (eventTypes.length === 0) {
      setError("At least one event type is required.");
      return;
    }

    if (!subscription.id) return;

    setSubmitting(true);
    const { data, error: apiError } = await api.PATCH(
      "/admin/clients/{clientId}/webhook-subscriptions/{id}",
      {
        params: { path: { clientId, id: subscription.id } },
        body: {
          url: trimmedUrl,
          eventTypes: eventTypes as WebhookEventWire[],
        },
      },
    );

    if (apiError || !data) {
      setError(
        (apiError as { detail?: string } | undefined)?.detail ??
          "Failed to update subscription.",
      );
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    onOpenChange(false);
    onUpdated(data);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit webhook subscription</DialogTitle>
            <DialogDescription>
              Admin override. Use the row toggle to disable a subscription — this dialog edits the
              endpoint URL and event types.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="webhook-url">URL</Label>
              <Input
                id="webhook-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/webhooks"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="webhook-event-types">Event types</Label>
              <Textarea
                id="webhook-event-types"
                value={eventTypesText}
                onChange={(e) => setEventTypesText(e.target.value)}
                rows={6}
                placeholder={"invoice.created\ninvoice.paid"}
              />
              <p className="text-xs text-muted-foreground">
                One per line, or comma-separated.
              </p>
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
